import { PERMISSIONS } from '../constants/permissions';

export interface DecodedToken {
  iss: string;
  sub: string;
  permissoes: string[];
  usuario_id: number;
  exp: number;
}

// Função para decodificar JWT sem dependência externa
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar JWT:', error);
    return null;
  }
}

class PermissionManager {
  private permissions: any = {};
  private decodedToken: DecodedToken | null = null;

  // Decodifica o token JWT e extrai as permissões
  decodeToken(token: string): DecodedToken | null {
    try {
      const decoded = decodeJWT(token) as DecodedToken;
      this.decodedToken = decoded;
      
      // Converte array de permissões para objeto
      this.permissions = {};
      decoded.permissoes.forEach(permission => {
        this.permissions[permission as keyof any] = true;
      });
      
      console.log('🔐 Permissões decodificadas:', this.permissions);
      return decoded;
    } catch (error) {
      console.error('❌ Erro ao decodificar token:', error);
      return null;
    }
  }

  // Verifica se o usuário tem uma permissão específica
  hasPermission(permission: keyof any): boolean {
    return !!this.permissions[permission];
  }

  // Verifica se o usuário tem pelo menos uma das permissões
  hasAnyPermission(permissions: (keyof any)[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  // Verifica se o usuário tem todas as permissões
  hasAllPermissions(permissions: (keyof any)[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  // Retorna todas as permissões do usuário
  getAllPermissions(): any {
    return { ...this.permissions };
  }

  // Retorna o ID do usuário
  getUserId(): number | null {
    return this.decodedToken?.usuario_id || null;
  }

  // Retorna o nome do usuário
  getUsername(): string | null {
    return this.decodedToken?.sub || null;
  }

  // Verifica se o token está expirado
  isTokenExpired(): boolean {
    if (!this.decodedToken) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return this.decodedToken.exp < currentTime;
  }

  // Limpa as permissões (para logout)
  clearPermissions(): void {
    this.permissions = {};
    this.decodedToken = null;
  }

  // Mapeamento de permissões para funcionalidades
  canAccessCursos(): boolean {
    return this.hasAnyPermission(['CURSO', 'GESTAO_CURSO', 'PROFESSOR']);
  }

  canAccessTratamentos(): boolean {
    return this.hasAnyPermission(['GESTAO_TRATAMENTO', 'ATENDENTE', 'TRATAMENTO']);
  }

  canAccessAssociados(): boolean {
    return this.hasAnyPermission(['ADMINISTRATIVO', 'ASSOCIADO_CONTATO']);
  }

  canAccessDebitos(): boolean {
    return this.hasAnyPermission([
      PERMISSIONS.GESTAO_DEBITO,
      PERMISSIONS.DEBITO,
      PERMISSIONS.ADMINISTRATIVO,
    ]);
  }

  canAccessAgendamentos(): boolean {
    return this.hasAnyPermission(['TRATAMENTO', 'GESTAO_AGENDA', 'ATENDENTE']);
  }

  /** Visibilidade do grupo de menu RELATÓRIOS (núcleo e jurídico). */
  canAccessRelatorios(): boolean {
    if (this.isSuperAdmin()) return true;
    return this.hasAnyPermission([
      PERMISSIONS.GESTAO_NUCLEO,
      PERMISSIONS.GESTAO_CURSO,
      PERMISSIONS.GESTAO_TRATAMENTO,
    ]);
  }

  canAccessRelatoriosFinanceirosPage(): boolean {
    if (this.isSuperAdmin()) return true;
    return this.hasAnyPermission([
      PERMISSIONS.GESTAO_DEBITO,
      PERMISSIONS.DEBITO,
      PERMISSIONS.ADMINISTRATIVO,
      PERMISSIONS.JURIDICO_RELATORIO_FINANCEIRO,
      PERMISSIONS.GESTAO_TRATAMENTO,
      PERMISSIONS.ATENDENTE,
      PERMISSIONS.TRATAMENTO,
    ]);
  }

  canGenerateRelatoriosFinanceirosSetor(): boolean {
    if (this.isSuperAdmin()) return true;
    return this.hasAnyPermission([
      PERMISSIONS.GESTAO_DEBITO,
      PERMISSIONS.DEBITO,
      PERMISSIONS.ADMINISTRATIVO,
      PERMISSIONS.JURIDICO_RELATORIO_FINANCEIRO,
    ]);
  }

  canGenerateRelatorioProcedimentoAberto(): boolean {
    if (this.isSuperAdmin()) return true;
    return this.hasAnyPermission([
      PERMISSIONS.GESTAO_DEBITO,
      PERMISSIONS.DEBITO,
      PERMISSIONS.ADMINISTRATIVO,
      PERMISSIONS.GESTAO_TRATAMENTO,
      PERMISSIONS.ATENDENTE,
      PERMISSIONS.TRATAMENTO,
    ]);
  }

  canManageCursos(): boolean {
    return this.hasPermission('GESTAO_CURSO');
  }

  canManageTratamentos(): boolean {
    return this.hasPermission('GESTAO_TRATAMENTO');
  }

  canManageDebitos(): boolean {
    return this.hasPermission('GESTAO_DEBITO');
  }

  canAccessOcorrencias(): boolean {
    if (this.isSuperAdmin()) return true;
    return this.hasAnyPermission([
      PERMISSIONS.USUARIO_CONSULTAR_OCORRENCIA,
      PERMISSIONS.GESTAO_ASSOCIADO,
    ]);
  }

  canManageOcorrencias(): boolean {
    if (this.isSuperAdmin()) return true;
    return this.hasPermission(PERMISSIONS.GESTAO_ASSOCIADO);
  }

  isSuperAdmin(): boolean {
  // return this.hasAllPermissions([
  //   'ADMINISTRATIVO',
  //   'ASSOCIADO_CONTATO',
  //   'ATENDENTE',
  //   'CURSO',
  //   'GESTAO_ASSOCIADO',
  //   'GESTAO_CURSO',
  //   'GESTAO_DEBITO',
  //   'GESTAO_TRATAMENTO',
  //   'JURIDICO',
  //   'NUCLEO',
  //   'PROFESSOR'
  // ]);
  return this.hasPermission('SUPER_ADMINISTRADOR')
}


  isAdmin(): boolean {
    return this.hasPermission('ADMINISTRATIVO');
  }

  isProfessor(): boolean {
    return this.hasPermission('PROFESSOR');
  }

  isOnlyProfessor(): boolean {
    const allPerms = Object.entries(this.permissions);
    return (
      allPerms.length > 0 &&
      allPerms.every(([key, value]) => {
        return key === 'PROFESSOR' ? value === true : value === false;
      })
    );
  }

  notProfessor() : boolean {
    return !this.isProfessor();
  }

  isAtendente(): boolean {
    return this.hasPermission('ATENDENTE');
  }

  isAssociadoContato(): boolean {
    return this.hasPermission('ASSOCIADO_CONTATO');
  }
}

// Instância singleton
export const permissionManager = new PermissionManager(); 